import { getDatabaseWriterPorts } from '@blocksuite/affine/blocks/database';
import { PublicationError } from '@streamlain/doc-publication-contract';
import type {
  DatabaseImportInput,
  DatabaseImportResult,
} from '@streamlain/doc-publication-contract/database-writer';

import {
  type LocalShardedDatabaseImportFromBatchesOptions,
  type LocalShardedDatabaseImportResult,
  prepareShardedImportTargets,
  throwIfLocalDatabaseImportAborted,
} from './local-sharded-importer';

export type WorkerShardedImportOptions = Omit<
  LocalShardedDatabaseImportFromBatchesOptions,
  'readNextBatch'
> & {
  input: DatabaseImportInput;
  /** Diagnostics are delivered once; never a worker progress barrier. */
  onApplyMetrics?: (metrics: {
    ackApplyMs: number;
    receiptCollectMs: number;
  }) => void;
  onWorkerResult?: (result: DatabaseImportResult) => void;
};

/** Main thread owns admission and ACK application; all row work is one autonomous worker job. */
export async function importShardedDatabasesInWorker(
  options: WorkerShardedImportOptions
): Promise<LocalShardedDatabaseImportResult> {
  const importer = getDatabaseWriterPorts(options.collection).importer;
  if (!importer) throw new PublicationError('FORMAT_MISMATCH');
  const prepared = await prepareShardedImportTargets(options);
  const jobId = crypto.randomUUID();
  let started = false;
  let timer: ReturnType<typeof setInterval> | undefined;
  let polling = false;
  const cancel = () => {
    void importer.cancel(jobId).catch(() => {});
  };
  try {
    throwIfLocalDatabaseImportAborted(options.signal);
    const targets = [...prepared.dataSourcesByClientId].map(
      ([clientId, { dataSource, columnIds }]) => ({
        clientId,
        rootDocId: dataSource.storageModel.store.id,
        databaseId: dataSource.databaseId,
        blockId: dataSource.storageModel.id,
        columnIds: [...columnIds],
        rowCount: options.sources.find(s => s.clientId === clientId)!.rowCount,
      })
    );
    if (targets.length !== options.sources.length)
      throw new PublicationError('CANCELLED');
    await importer.start({
      jobId,
      workspaceId: options.collection.id,
      targets,
      input: options.input,
      liveClientIds: [...options.collection.docs.values()].map(
        doc => doc.spaceDoc.clientID
      ),
      userId:
        prepared.dataSourcesByClientId.values().next().value?.dataSource
          .importWriterId ?? null,
    });
    started = true;
    options.signal?.addEventListener('abort', cancel, { once: true });
    if (options.signal?.aborted) cancel();
    // Optional sampling has no connection to the worker's scheduling or ACKs.
    if (options.onProgress || options.syncProgress)
      timer = setInterval(() => {
        if (polling) return;
        polling = true;
        void importer
          .status(jobId)
          .then(status => {
            options.syncProgress?.local(status.readRows, status.processedRows);
            options.onProgress?.({
              phase: 'rows-importing',
              processedRows: status.processedRows,
              totalRows: status.totalRows,
              percent: status.totalRows
                ? Math.floor((100 * status.processedRows) / status.totalRows)
                : null,
            });
          })
          .catch(() => {})
          .finally(() => {
            polling = false;
          });
      }, 100);
    const result = await importer.result(jobId);
    options.syncProgress?.packets(result.measurements);
    options.onWorkerResult?.(result);
    if (result.status !== 'completed')
      throw Object.assign(
        new PublicationError(result.error ?? 'FORMAT_MISMATCH'),
        {
          failureStage: result.failureStage,
          failureName: result.failureName,
          failureMessage: result.failureMessage,
        }
      );
    const applyStarted = performance.now();
    for (const [clientId, { dataSource }] of prepared.dataSourcesByClientId)
      await dataSource.applyArchivedImport(
        importer,
        jobId,
        result.commits.filter(c => c.clientId === clientId)
      );
    const ackApplyMs = performance.now() - applyStarted;
    options.syncProgress?.applied(ackApplyMs);
    const receiptStarted = performance.now();
    prepared.receipts.push(...result.commits.map(c => c.receipt));
    options.onApplyMetrics?.({
      ackApplyMs,
      receiptCollectMs: performance.now() - receiptStarted,
    });
    options.syncProgress?.savedLocally(result.processedRows);
    options.onProgress?.({
      phase: 'import-finalizing',
      processedRows: result.processedRows,
      totalRows: prepared.totalImportRows,
      percent: 100,
    });
    return {
      docId: options.docId,
      doc: prepared.doc,
      databaseBlockIds: prepared.databaseBlockIds,
      receipts: prepared.receipts,
      stats: {
        databaseCount: prepared.databaseBlockIds.length,
        totalRows: result.processedRows,
      },
    };
  } finally {
    if (timer) clearInterval(timer);
    options.signal?.removeEventListener('abort', cancel);
    try {
      if (started) await importer.release(jobId);
      else if (options.input.kind === 'port') options.input.port.close();
    } finally {
      await prepared.dispose();
    }
  }
}
